import React, { useState, useContext } from 'react';
import { find, get, some } from 'lodash';
import { DataGridWrapper } from '../../../../Utils/DataGridWrapper/DataGridWrapper';
import { getCertificationDashboardDataAPI, getCertificationStatusStatsAPI } from '../../../../Services/api';
import { I18nReactContext } from '../../../../contexts/I18nContext';
import { GridReactContext } from '../../../../Utils/DataGridWrapper/GridContext';
import { UserDetailsCell } from '../../../Settings/DeletedUsers/UserDetailsCell/UserDetailsCell';
import { PieChart } from '../../../Charts/PieChart/PieChart';
import { Colors } from '../../../../Utils/styles/Colors';
import { getSignDocOptions } from '../../SignDocsStats/SignDocsStatsLib';
import { ProgressBar } from '../../../ProgressBar/ProgressBar';
import { LocalPopover } from '../../../../Utils/Popover/Popover';
import { CertificationStatusBadge } from '../CertificationStatusBadge/CertificationStatusBadge';
import { SignDocCategories } from '../../../../Utils/Constants';
import { PhoneCell } from '../../../../Utils/PhoneCell/PhoneCell';
import './CertificationDashboardTable.scss';
import {UserReactContext} from '../../../../contexts/UserContext';



/**
 * Gets the column configuration for the certification dashboard table
 *
 * @param {object} params - The function parameters container object
 * @param {object} params.i18n - Translation context object
 * @param {boolean} params.isCompanyAdmin - Whether the current user is a company admin
 * @returns {Array} Array of column configurations for the data grid
 */
const getCertificationDashboardColumns = ({ i18n, isCompanyAdmin }) => {
    return [
        {
            field: 'firstName',
            headerName: i18n.translate("certificationDashboard.columns.fullName"),
            minWidth: 160,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{`${params.value} ${params.row.lastName}`}</div>,
        },
        {
            field: 'phoneFormatted',
            headerName: i18n.translate('certificationDashboard.columns.phone'),
            minWidth: 150,
            align: 'right',
            sortable: true,
            renderCell: (params) => <PhoneCell value={params.value} />,
        },
        {
            field: 'organizations',
            headerName: i18n.translate('certificationDashboard.columns.organizations'),
            minWidth: 250,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                const organizations = get(params.row, 'organizations', []);
                return organizations.length === 0 ? <div style={{ width: '100%', color: '#999' }}>-</div>
                : (
                    <div className="organizationsWrapper">
                        {organizations.map((org, index) => (
                            <span key={index} className="organizationName">
                                {org.orgName}
                            </span>
                        ))}
                    </div>
                );
            },
        },
        {
            field: 'idNumber',
            headerName: i18n.translate('certificationDashboard.columns.idNumber'),
            minWidth: 140,
            align: 'right',
            sortable: true,
            renderCell: (params) => <div style={{ width: '100%' }} title={params.value}>{params.value || '-'}</div>,
        },
        {
            field: 'employeeId',
            headerName: i18n.translate('certificationDashboard.columns.employeeId'),
            minWidth: 140,
            align: 'right',
            sortable: true,
            renderCell: (params) => <div style={{ width: '100%' }} title={params.value}>{params.value || '-'}</div>,
        },
        {
            field: 'urgencyStatus',
            headerName: i18n.translate('certificationDashboard.columns.status'),
            minWidth: 150,
            align: 'center',
            sortable: true,
            renderCell: (params) => {
                const status = params.row.urgencyStatus || 'INCOMPLETE';
                return <CertificationStatusBadge status={status} />;
            },
        },
        {
            field: 'progressPercentage',
            headerName: i18n.translate('certificationDashboard.columns.chosenOrganizationUnitsProgress'),
            minWidth: 240,
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                if (params.row.isCompanyAdmin && !isCompanyAdmin) {
                    return <div style={{ width: '100%' }} title={params.value}>-</div>;
                }
                const percentage = params.value || 0;
                const completedAssignments = get(params.row, 'completedAssignments', 0);
                const totalAssignments = get(params.row, 'totalAssignments', 0);

                return (
                    <div className="progressContainer">
                        <ProgressBar
                            percentage={percentage}
                            size="medium"
                            className="ProgressBar--table-cell"
                            containerStyle={{ width: '70%', justifyContent: 'flex-start' }}
                            statusText={`${completedAssignments}/${totalAssignments} ${i18n.translate('certificationDashboard.tasksCompleted')}`}
                        />
                    </div>
                );
            },
        },
        {
            field: 'allManagedProgressPercentage',
            headerName: i18n.translate('certificationDashboard.columns.allManagedProgress'),
            minWidth: 240,
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                if (params.row.isCompanyAdmin && !isCompanyAdmin) {
                    return <div style={{ width: '100%' }} title={params.value}>-</div>;
                }
                const percentage = params.value || 0;
                const completedAssignments = get(params.row, 'allManagedCompletedAssignments', 0);
                const totalAssignments = get(params.row, 'allManagedTotalAssignments', 0);

                return (
                    <div className="progressContainer">
                        <ProgressBar
                            percentage={percentage}
                            size="medium"
                            className="ProgressBar--table-cell"
                            containerStyle={{ width: '70%', justifyContent: 'flex-start' }}
                            statusText={`${completedAssignments}/${totalAssignments} ${i18n.translate('certificationDashboard.tasksCompleted')}`}
                        />
                    </div>
                );
            },
        },
        {
            field: 'userDetails',
            headerName: i18n.translate('certificationDashboard.columns.userDetails'),
            minWidth: 130,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                if (params.row.isCompanyAdmin && !isCompanyAdmin) {
                    return <div style={{ width: '100%' }} title={params.value}>-</div>;
                }
                return <UserDetailsCell user={params.row}/>;
            },
        }
    ];
};

/**
 * Converts user data to the format expected by the data grid
 * Note: DataGridWrapper passes the users array directly, not the full API response
 *
 * @param {Array} users - Array of user objects from the API
 * @returns {Array} Array of user data formatted for the data grid
 */
const convertData = (users) => {
    if (!Array.isArray(users)) {
        return [];
    }

    // Ensure each user has the required fields for DataGrid and UserDetailsCell
    return users.map((user, index) => {
        return {
            id: get(user, 'id', `user-${index}`),
            ...user
        };
    });
};

/**
 * Prepares pie chart data for certification status visualization
 *
 * @param {object} params - Function parameters
 * @param {object} params.stats - Certification status statistics (EXPIRED, EXPIRING_SOON, INCOMPLETE, COMPLETED)
 * @param {object} params.i18n - Translation context object
 * @returns {Array} Array of pie chart data objects
 */
const preparePieChartData = ({ stats, i18n }) => {
    const { EXPIRED, EXPIRING_SOON, INCOMPLETE, COMPLETED, total } = stats;

    if (total === 0) {
        return [];
    }

    const data = [
        {
            label: i18n.translate('certificationStatus.expired'),
            value: EXPIRED || 0,
            color: Colors["$failedColor"] // Red - matching SignDocsStats
        },
        {
            label: i18n.translate('certificationStatus.expiringSoon'),
            value: EXPIRING_SOON || 0,
            color: Colors["$GraphBackground2"] // Yellow - matching SignDocsStats
        },
        {
            label: i18n.translate('certificationStatus.incomplete'),
            value: INCOMPLETE || 0,
            color: Colors["$GraphBackground3"] // Gray - matching SignDocsStats
        },
        {
            label: i18n.translate('certificationStatus.completed'),
            value: COMPLETED || 0,
            color: Colors["$GraphBackground1"] // Green - matching SignDocsStats
        },
    ].filter(item => item.value > 0); // Only show statuses with values

    return data;
};

/**
 * Default pagination model for the data grid
 */
const paginationModel = {
    page: 0,
    pageSize: 50,
};

/**
 * Default table filters configuration
 */
const tableFilters = {
    showFilters: {
        search: true,
        organizations: true,
        signDocs: true
    },
    filterValues: {},
    filterData: {}
};

/**
 * CertificationDashboardTable Component
 *
 * Displays a table of users with their certification progress percentages based on completed certifications
 * from organizations the admin manages. Supports pagination, filtering, and sorting.
 *
 * @param {object} params - Component parameters
 * @param {Array} params.organizationsOptions - Array of organization options for filtering
 * @param {Array} params.adminOrganizations - Full organization objects with signDoc data
 * @param {React.Component} params.tableButtons - Optional buttons to display above the table
 * @returns {JSX.Element} Rendered certification dashboard table component
 */
export const CertificationDashboardTable = ({ organizationsOptions, adminOrganizations, tableButtons }) => {
    const { i18n } = useContext(I18nReactContext);
    const userContext = useContext(UserReactContext)
    const gridContext = useContext(GridReactContext);
    const [pieChartData, setPieChartData] = useState([]);
    const [allManagedPieChartData, setAllManagedPieChartData] = useState([]);

    // Initialize complete filter state with both organizations and signDocs
    const initializeFilterModel = () => {
        const validOrganizationsOptions = Array.isArray(organizationsOptions) ? organizationsOptions : [];

        // Filter organizations to show only those with certification signDocs
        const orgsWithCertifications = validOrganizationsOptions.filter(orgOption => {
            // Keep the 'all' option
            if (orgOption.value === 'all') return true;

            // Find the organization in adminOrganizations
            const orgData = adminOrganizations.find(org => get(org, 'organization.id') === orgOption.value);
            if (!orgData) return false;

            // Check if this organization has any certification category signDocs
            const orgSignDocs = get(orgData, 'organization.signDocs', []);
            const hasCertifications = some(orgSignDocs, signDoc => {
                const orgSignDoc = find(signDoc.organizationSignDoc, {OrganizationId: orgOption.value});
                return get(orgSignDoc, 'isValid') && get(signDoc, 'category') === SignDocCategories.CERTIFICATION;
            });

            return hasCertifications;
        });

        const firstOrgValue = get(orgsWithCertifications, '[0].value') || 'all';

        // Get initial signDoc options based on the first organization, filtered by CERTIFICATION category
        const { signDocOptionsTmp: certificationSignDocs } = getSignDocOptions({
            selectedOrgOptions: [firstOrgValue],
            adminOrganizations,
            categories: [SignDocCategories.CERTIFICATION], // Filter by CERTIFICATION category
            i18n
        });

        return {
            ...tableFilters,
            filterData: {
                ...tableFilters.filterData,
                organizations: orgsWithCertifications,
                organizationsFull: adminOrganizations, // Pass full organization data for DataGridWrapper updates
                signDocs: certificationSignDocs,
                categories: [SignDocCategories.CERTIFICATION] // Pass categories for DataGridWrapper to use when updating signDoc options
            },
            filterValues: {
                ...tableFilters.filterValues,
                organizationIds: [firstOrgValue],
                signDocId: 'all'
            }
        };
    };

    const [localTableFilters, setLocalTableFilters] = useState(() => initializeFilterModel());

    /**
     * Fetches pie chart data from the API using the current filter model from GridContext
     */
    const fetchPieChartData = async (filterModel = null) => {
        try {
            // Use the filter model from GridContext if available, otherwise use local filters
            const currentFilterModel = filterModel || gridContext.filterModel || localTableFilters;

            const result = await getCertificationStatusStatsAPI({
                filterModel: currentFilterModel
            });

            if (result && result.data) {
                // Process selected organizations data - use certification status structure
                const selectedChartData = preparePieChartData({ stats: result.data, i18n });
                setPieChartData(selectedChartData);

                // Process all managed organizations data (Step 11: Second Pie Chart)
                if (result.allManagedData) {
                    const allManagedChartData = preparePieChartData({ stats: result.allManagedData, i18n });
                    setAllManagedPieChartData(allManagedChartData);
                } else {
                    setAllManagedPieChartData([]);
                }
            } else {
                console.error('Invalid API response structure:', result);
                setPieChartData([]);
                setAllManagedPieChartData([]);
            }
        } catch (error) {
            console.error('Error fetching pie chart data:', error);
            setPieChartData([]);
            setAllManagedPieChartData([]);
        }
    };
    /**
     * Gets the columns configuration for the table
     *
     * @param {object} params - Function parameters
     * @param {object} params.paginationModel - Pagination settings
     * @param {object} params.filterModel - Filter settings
     * @returns {Promise<Array>} Promise that resolves to array of column configurations
     */
    const getColumns = async () => {
        return getCertificationDashboardColumns({ i18n, isCompanyAdmin: userContext?.isManagingCompany() });
    };

    /**
     * API call to get certification dashboard data
     *
     * @param {object} params - Function parameters
     * @param {object} params.paginationModel - Pagination settings
     * @param {object} params.filterModel - Filter settings
     * @param {object} params.sortModel - Sort settings
     * @returns {Promise} API response with certification dashboard data
     */
    const apiToGetData = async ({ paginationModel, filterModel, sortModel }) => {
        let organizationIds = get(filterModel, 'filterValues.organizationIds', []);
        let result = { data: [], count: 0 };
        fetchPieChartData(filterModel);
        if (organizationIds.length > 0) {
            result = await getCertificationDashboardDataAPI({
                paginationModel,
                filterModel,
                sortModel
            });

        }
        return result;
    };

    return (
        <div className="CertificationDashboardTable">
            <div className="CertificationDashboardTable__charts-section">
                {pieChartData.length > 0 && (
                    <div className="CertificationDashboardTable__chart-section">
                        <div className="CertificationDashboardTable__info-bubble">
                            <LocalPopover
                                info={i18n.translate('certificationDashboard.pieChart.selectedOrgsInfoBubble')}
                            />
                        </div>
                        <h3 className="CertificationDashboardTable__chart-title">
                            {i18n.translate('certificationDashboard.pieChart.selectedOrgsTitle')}
                        </h3>
                        <div className="CertificationDashboardTable__chart-container">
                            <PieChart
                                data={pieChartData}
                                questionText={String(i18n.translate('certificationDashboard.pieChart.selectedOrgsSubtitle'))}
                                variant="doughnut"
                                showPercentages={true}
                                excludeZeroValues={true}
                                showLegend={true}
                                height={300}
                            />
                        </div>
                    </div>
                )}

                {allManagedPieChartData.length > 0 && (
                    <div className="CertificationDashboardTable__chart-section">
                        <div className="CertificationDashboardTable__info-bubble">
                            <LocalPopover
                                info={i18n.translate('certificationDashboard.pieChart.allManagedInfoBubble')}
                            />
                        </div>
                        <h3 className="CertificationDashboardTable__chart-title">
                            {i18n.translate('certificationDashboard.pieChart.allManagedTitle')}
                        </h3>
                        <div className="CertificationDashboardTable__chart-container">
                            <PieChart
                                data={allManagedPieChartData}
                                questionText={String(i18n.translate('certificationDashboard.pieChart.allManagedSubtitle'))}
                                variant="doughnut"
                                showPercentages={true}
                                excludeZeroValues={true}
                                showLegend={true}
                                height={300}
                            />
                        </div>
                    </div>
                )}
            </div>

            <DataGridWrapper
                buttons={tableButtons}
                filterModel={localTableFilters}
                disableColumnFilter={true}
                paginationModel={paginationModel}
                sortModel={[]}
                checkboxSelection={false}
                onSelectionModelChange={null}
                selectionModel={[]}
                isRowSelectable={null}
                getColumns={getColumns}
                apiToGetData={apiToGetData}
                dataConverter={convertData}
            />
        </div>
    );
};

export default CertificationDashboardTable;
